import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  type BctTheme,
  type NotificationPreference,
  type UserRole,
  useAuthStore,
} from '@/stores/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

interface AuthMeResponse {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string | null;
  profilePictureUrl: string | null;
  authProvider: 'EMAIL' | 'MICROSOFT' | 'GOOGLE';
  isActive: boolean;
  preferredLanguage?: string;
  notificationPreference?: NotificationPreference;
  // Contact & Availability fields
  phoneNumber?: string | null;
  phoneVisible?: boolean;
  weeklyAttendance?: Record<string, string | null> | null;
  attendanceVisible?: boolean;
  organizationTheme?: BctTheme;
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const {
    user,
    accessToken,
    isLoading,
    setUser,
    setLoading,
    clearAuth,
    setAccessToken,
    setLoginContext,
  } = useAuthStore();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      // If we already have a user, we're authenticated
      if (user) {
        setIsInitialized(true);
        setLoading(false);
        return;
      }

      // If we have a token but no user, try to fetch user data
      if (accessToken) {
        try {
          const response = await api.get<AuthMeResponse>('/auth/me');

          if (response.data) {
            setUser(response.data);
            setLoginContext('refresh');
          }
        } catch {
          // Token is invalid, clear auth state
          clearAuth();
        }
      } else {
        // Try to refresh the token
        try {
          const response = await fetch('/api/v1/auth/refresh', {
            method: 'POST',
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            if (data.data?.accessToken) {
              setAccessToken(data.data.accessToken);

              // Fetch user data with new token
              const userResponse = await api.get<AuthMeResponse>('/auth/me');

              if (userResponse.data) {
                setUser(userResponse.data);
                setLoginContext('refresh');
              }
            }
          }
        } catch {
          // Refresh failed, user needs to login
        }
      }

      setIsInitialized(true);
      setLoading(false);
    }

    checkAuth();
  }, [accessToken, user, setUser, setLoading, clearAuth, setAccessToken, setLoginContext]);

  // Show loading state while checking auth
  if (isLoading || !isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!(user && accessToken)) {
    // Store intended destination for redirect after login
    sessionStorage.setItem('auth:redirect', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      // User doesn't have required role - redirect to dashboard
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
